import { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Link2, Eye, Trash2, Loader2, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { projectAPI } from '@/utils/supabase/client';
import {
  DocumentSummary,
  DocumentType,
  DOCUMENT_TYPE_LABELS,
} from '@/types/documents';
import { toast } from 'sonner';

interface DocumentsPanelProps {
  prospectId?: string;
  clientId?: string;
}

interface Engagement {
  views: number;
  lastViewed: string | null;
}

/**
 * Documents panel for a prospect or client. Lists linked docs, creates new ones,
 * publishes, copies a public share link, and surfaces engagement. Admin/owner only
 * (the API enforces it). Renders nothing destructive without confirmation.
 */
export function DocumentsPanel({ prospectId, clientId }: DocumentsPanelProps) {
  const [docs, setDocs] = useState<DocumentSummary[]>([]);
  const [engagement, setEngagement] = useState<Record<string, Engagement>>({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Create form
  const [title, setTitle] = useState('');
  const [type, setType] = useState<DocumentType>('deck');
  const [externalUrl, setExternalUrl] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await projectAPI.getDocuments({ prospect_id: prospectId, client_id: clientId });
      setDocs(list);
      // engagement for published docs (small N per record)
      const published = list.filter((d) => d.status === 'published');
      const entries = await Promise.all(
        published.map(async (d) => {
          try {
            const e = await projectAPI.getDocumentEngagement(d.id);
            const last = e.engagement.reduce<string | null>(
              (acc, r) => (r.last_viewed_at && (!acc || r.last_viewed_at > acc) ? r.last_viewed_at : acc),
              null,
            );
            return [d.id, { views: e.total_views, lastViewed: last }] as const;
          } catch {
            return [d.id, { views: 0, lastViewed: null }] as const;
          }
        }),
      );
      setEngagement(Object.fromEntries(entries));
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  }, [prospectId, clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      await projectAPI.createDocument({
        type,
        title: title.trim(),
        external_url: externalUrl.trim() || null,
        prospect_id: prospectId || null,
        client_id: clientId || null,
      });
      setTitle('');
      setExternalUrl('');
      setType('deck');
      toast.success('Document created');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create document');
    } finally {
      setCreating(false);
    }
  };

  const togglePublish = async (doc: DocumentSummary) => {
    setBusyId(doc.id);
    try {
      const next = doc.status === 'published' ? 'draft' : 'published';
      await projectAPI.updateDocument(doc.id, { status: next });
      toast.success(next === 'published' ? 'Published' : 'Unpublished');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setBusyId(null);
    }
  };

  const copyShareLink = async (doc: DocumentSummary) => {
    setBusyId(doc.id);
    try {
      const detail = await projectAPI.getDocument(doc.id);
      const active = detail.shares.find((s) => !s.revoked);
      const share = active || (await projectAPI.createDocumentShare(doc.id));
      const url = `${window.location.origin}/p/${share.public_token}`;
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create share link');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (doc: DocumentSummary) => {
    setBusyId(doc.id);
    try {
      await projectAPI.deleteDocument(doc.id);
      toast.success('Document deleted');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3">
      {/* Create row */}
      <div className="flex flex-col gap-2 rounded-md border border-dashed border-border p-3 sm:flex-row sm:items-center">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Document title"
          className="sm:flex-1"
        />
        <Select value={type} onValueChange={(v) => setType(v as DocumentType)}>
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[]).map((t) => (
              <SelectItem key={t} value={t}>
                {DOCUMENT_TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={handleCreate} disabled={creating || !title.trim()} className="shrink-0">
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          <span className="ml-1">Add</span>
        </Button>
      </div>
      <Input
        value={externalUrl}
        onChange={(e) => setExternalUrl(e.target.value)}
        placeholder="Optional: hosted URL (PDF, Drive, Stripe)"
        className="text-sm"
      />

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : docs.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No documents linked yet. Add one above to share it.
        </p>
      ) : (
        <div className="space-y-2">
          {docs.map((doc) => {
            const eng = engagement[doc.id];
            const busy = busyId === doc.id;
            return (
              <div key={doc.id} className="rounded-md border border-border p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate text-sm font-medium">{doc.title}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className="text-xs">{DOCUMENT_TYPE_LABELS[doc.type]}</Badge>
                      <Badge
                        variant={doc.status === 'published' ? 'success' : 'secondary'}
                        className="text-xs"
                      >
                        {doc.status}
                      </Badge>
                      {doc.external_url && <Globe className="h-3.5 w-3.5 text-muted-foreground" />}
                      {eng && eng.views > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Eye className="h-3 w-3" />
                          viewed {eng.views}×
                          {eng.lastViewed && ` · ${new Date(eng.lastViewed).toLocaleDateString()}`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => togglePublish(doc)} disabled={busy}>
                    {doc.status === 'published' ? 'Unpublish' : 'Publish'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyShareLink(doc)}
                    disabled={busy || doc.status !== 'published'}
                    title={doc.status !== 'published' ? 'Publish first to share' : 'Copy public link'}
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    <span className="ml-1">Copy link</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(doc)}
                    disabled={busy}
                    className="text-destructive hover:text-destructive"
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
